import { useState, useContext, useEffect, useRef } from 'react';
import styles from './QuickViewV2.module.scss';
import { QuickViewContext } from '../../../contexts/QuickViewContext';
import useIntersectionV2 from 'lib/hooks/useIntersectionv2';
import { Button } from '@americastestkitchen/mise';
import {
  disableBodyScroll,
  enableBodyScroll,
  clearAllBodyScrollLocks,
} from 'body-scroll-lock';
import { useRouter } from 'next/router';
// import Button from '../../elements/Button';
import classnames from 'classnames/bind';

const cx = classnames.bind(styles);
interface QuickViewV2Props {
  quickViewContent: React.ReactElement | null;
}

const QuickViewV2: React.FC<QuickViewV2Props> = ({
  quickViewContent,
}: QuickViewV2Props) => {
  const { closeQuickView, isOpen, headerVisible } =
    useContext(QuickViewContext);
  const router = useRouter();
  const [slideIn, setSlideIn] = useState<boolean>(true);
  const [disableScroll, setDisableScroll] = useState<boolean>(false);
  const quickViewBodyRef = useRef<HTMLElement | null>(null);
  const scrollContentRef = useRef<HTMLDivElement | null>(null);
  const backgroundLockRef = useRef<HTMLDivElement | null>(null);

  const quickViewVisible = useIntersectionV2(quickViewBodyRef, {
    root: null,
    rootMargin: '0px',
    threshold: 1.0,
  });

  const panelClasses = cx({
    panel: true,
    'panel--sticky': !headerVisible && !disableScroll,
    'panel--disable-scroll': disableScroll,
    slideIn: slideIn,
  });

  const frameClasses = cx({
    frame: true,
    'frame--fullHeight': !headerVisible && !disableScroll,
    'frame--headerVisibleHeight': headerVisible && !disableScroll,
  });

  const outerClasses = cx(styles.outer, {
    features: router.asPath === '/features/shrimp-cocktail',
  });

  const determineDeviceType = (viewWidth: number) => {
    let deviceType = 'mobile';
    if (viewWidth > 768) {
      deviceType = viewWidth > 1024 ? 'desktop' : 'tablet';
    }
    return deviceType;
  };

  useEffect(() => {
    const focusFirst = () => {
      if (!scrollContentRef.current) return;
      const target = scrollContentRef.current.querySelector(
        `h2, h3, h4, h5`,
      ) as HTMLHeadingElement;
      {
        if (target && !target.getAttribute('tabindex')) {
          target.setAttribute('tabindex', '0');
          target.focus({ preventScroll: true });
        }
      }
    };
    focusFirst();

    const handleResize = () => {
      if (!scrollContentRef.current) {
        return;
      }
      if (
        determineDeviceType(window.innerWidth) === 'mobile' ||
        determineDeviceType(window.innerWidth) === 'tablet'
      ) {
        disableBodyScroll(scrollContentRef.current);
        setDisableScroll(true);
      } else {
        enableBodyScroll(scrollContentRef.current);
        setDisableScroll(false);
      }
    };
    window.addEventListener('resize', handleResize);
    handleResize();

    return () => {
      window.removeEventListener('resize', handleResize);
      clearAllBodyScrollLocks();
    };
  }, []);

  useEffect(() => {
    if (quickViewVisible) {
      setSlideIn(false);
    }
  }, [quickViewVisible]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' || event.key === 'x') {
        closeQuickView();
      }
    };
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [closeQuickView, isOpen]);

  return (
    <div className={outerClasses}>
      <div
        className={disableScroll ? styles.backgroundLock : ''}
        ref={backgroundLockRef}
      ></div>
      <aside
        ref={quickViewBodyRef}
        className={panelClasses}
        id="quickview_panel"
      >
        <div className={frameClasses}>
          <div className={styles.content}>
            <div className={styles.scrollContent} ref={scrollContentRef}>
              {quickViewContent}
            </div>
          </div>
          <div className={styles.buttonContainer}>
            <Button isFullWidth variant="primary" onClick={closeQuickView}>
              Close
            </Button>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default QuickViewV2;
